import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Globe,
  Calendar,
  MapPin,
  History,
  AlertTriangle,
  Link as LinkIcon,
  Gauge,
  ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { useTeams } from '../../contexts/TeamsContext';
import { BrandApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { DomainThreat, TakedownEvent } from 'api-client';
import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';

const DomainThreatDetails = () => {
  const { id } = useParams();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [threat, setThreat] = useState<DomainThreat | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThreat = useCallback(async () => {
    if (!currentTeam?.slug || !id) return;

    setLoading(true);
    try {
      const brandApi = new BrandApi(getApiConfiguration(token));
      const response = await brandApi.brandThreatsRetrieve({
        teamSlug: currentTeam.slug,
        id: parseInt(id),
      });
      setThreat(response);
      setError(null);
    } catch (err) {
      console.error('Error fetching domain threat:', err);
      setError('Failed to load domain threat details');
    } finally {
      setLoading(false);
    }
  }, [currentTeam?.slug, id, token]);

  useEffect(() => {
    fetchThreat();
  }, [fetchThreat]);

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'new':
        return 'default';
      case 'detected':
        return 'info';
      case 'investigating':
        return 'warning';
      case 'reported':
        return 'warning';
      case 'takendown':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !threat) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">{error || 'Domain threat not found'}</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="relative">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">{threat.threatDomain}</h1>
            <div className="flex items-center space-x-3">
              <Badge variant={getStatusColor(threat.status)}>
                {threat.statusDisplay}
              </Badge>
              <div className="text-sm text-gray-400">
                Case #{threat.id}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Key Information */}
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 space-y-6">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <Globe className="h-4 w-4" />
                  <span>Type</span>
                </div>
                <div className="text-white">{threat.threatTypeDisplay}</div>
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <LinkIcon className="h-4 w-4" />
                  <span>Affected Domain</span>
                </div>
                <div className="text-white">{threat.affectedDomain.domainName}</div>
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <Calendar className="h-4 w-4" />
                  <span>Created</span>
                </div>
                <div className="text-white">
                  {new Date(threat.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center space-x-2 text-gray-400 mb-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Description</span>
              </div>
              <div className="text-white whitespace-pre-wrap">
                {threat.description}
              </div>
            </div>

            {/* Timeline Events */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2 text-gray-400">
                  <History className="h-4 w-4" />
                  <span>Takedown Events</span>
                </div>
              </div>
              <div className="space-y-4">
                {threat.takedownEvents?.map((event: TakedownEvent) => (
                  <div
                    key={event.id}
                    className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-gray-400">
                        {new Date(event.eventDate).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-white whitespace-pre-wrap">
                      {event.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Screenshot/Evidence */}
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Evidence</h3>
            {threat.screenshot ? (
              <div className="aspect-video rounded-xl overflow-hidden bg-black/50 ring-1 ring-white/10">
                <img
                  src={threat.screenshot}
                  alt="Domain Threat Screenshot"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="aspect-video rounded-xl overflow-hidden bg-black/50 ring-1 ring-white/10 flex items-center justify-center">
                <div className="text-center p-6">
                  <div className="w-12 h-12 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-3">
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-400">No screenshot available</p>
                  <p className="text-xs text-gray-500 mt-1">Evidence is being processed or unavailable</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6">
            <AssetNotes objectId={threat.id.toString()} objectType="domainthreat" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainThreatDetails;
